import { useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { allComics, categories } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';

const categoryInfoMap: Record<string, { name: string; description: string }> = {
  hanman: { name: '韩漫', description: '精选韩国漫画，高质量彩漫，下拉式连续阅读免费体验。布丁漫画下拉式免费看最新韩漫全集。' },
  riman: { name: '日漫', description: '经典日漫大全，热血、恋爱、悬疑各种题材，布丁漫画下拉式连续阅读畅享全集。' },
  guoman: { name: '国漫', description: '国产漫画精品推荐，玄幻、都市、科幻应有尽有，布丁漫画下拉式免费阅读国漫全集。' },
  shaonian: { name: '少年漫', description: '热血少年漫画，友情、努力、胜利的经典主题，布丁漫画下拉式连续阅读体验。' },
  shaonv: { name: '少女漫', description: '浪漫少女漫画，甜蜜恋爱故事大集合，布丁漫画下拉式免费在线阅读。' },
  rexue: { name: '热血', description: '高燃热血漫画，战斗、竞技、冒险，让你热血沸腾！布丁漫画全集下拉式连续阅读。' },
  xuanhuan: { name: '玄幻', description: '奇幻玄幻漫画，修仙、魔法、异世界冒险，布丁漫画下拉式免费畅读全集。' },
  dushi: { name: '都市', description: '都市题材漫画，职场、校园、生活百态，布丁漫画下拉式连续阅读全本免费。' },
  xuanyi: { name: '悬疑', description: '悬疑推理漫画，烧脑谜题、惊悚案件，布丁漫画下拉式免费看全集。' },
  gaoxiao: { name: '搞笑', description: '欢乐搞笑漫画，轻松日常、爆笑喜剧，布丁漫画下拉式连续阅读减压首选。' },
  kehuan: { name: '科幻', description: '科幻漫画合集，星际冒险、未来世界，布丁漫画下拉式免费阅读全本。' },
  maoxian: { name: '冒险', description: '冒险漫画精选，奇幻旅程、惊险探索，布丁漫画下拉式连续阅读全集。' },
};

export default function CategoryPage() {
  const { slug = 'hanman' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(slug);

  const categoryInfo = categoryInfoMap[slug] || categoryInfoMap.hanman;

  const filteredComics = useMemo(() => {
    return allComics.filter((comic) => comic.category === slug);
  }, [slug]);

  const handleTabChange = (newSlug: string) => {
    setActiveTab(newSlug);
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `布丁漫画 - ${categoryInfo.name}分类`,
            description: categoryInfo.description,
            url: `https://budingmanhua.com/category/${slug}`,
            mainEntity: {
              '@type': 'ItemList',
              itemListElement: filteredComics.map((comic, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                item: {
                  '@type': 'CreativeWork',
                  name: comic.title,
                  url: `https://budingmanhua.com/comic/${comic.id}`,
                },
              })),
            },
          }),
        }}
      />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '分类浏览', path: '/category/hanman' },
            { label: categoryInfo.name },
          ]}
        />

        <section className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
              {categoryInfo.name}漫画
            </h1>
            <p className="text-foreground-500 text-sm mb-8">{categoryInfo.description}</p>

            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleTabChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200/80 border border-background-200/70'
                  }`}
                >
                  <span className="mr-1.5">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </button>
              ))}
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6" data-product-shop="">
                {filteredComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} - 布丁漫画${categoryInfo.name}下拉式免费阅读`}
                        title={`${comic.title} 布丁漫画下拉式免费${categoryInfo.name}全集`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      {comic.status === '已完结' && (
                        <div className="absolute top-2 right-2">
                          <span className="bg-foreground-800/80 text-white text-xs px-2 py-0.5 rounded-full">
                            已完结
                          </span>
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <span className="text-white text-xs">
                          共 {comic.chapters.length} 话
                        </span>
                      </div>
                    </div>
                    <h3 className="text-foreground-900 font-medium text-sm md:text-base truncate group-hover:text-primary-500 transition-colors">
                      {comic.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-foreground-500 text-xs">{comic.author}</span>
                      <span className="text-foreground-400 text-xs">·</span>
                      <span className="text-accent-500 text-xs flex items-center gap-0.5">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-star-fill text-[10px]"></i>
                        </div>
                        {comic.rating}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <i className="ri-emotion-sad-line text-foreground-300 text-4xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无漫画，请查看其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}